import * as THREE from "three/webgpu";
import {
  Fn,
  If,
  PI,
  TWO_PI,
  abs,
  clamp,
  cos,
  float,
  hash,
  instanceIndex,
  max,
  mix,
  sin,
  smoothstep,
  step,
  storage,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from "three/tsl";

export type GpuVfxKind = "artillery" | "tornado" | "flood" | "rift" | "catrush" | "mech" | "hound";

const GPU_VFX_CAPACITY = 8_192;
const KIND_ID: Record<GpuVfxKind, number> = {
  artillery: 1,
  tornado: 2,
  flood: 3,
  rift: 4,
  catrush: 5,
  mech: 6,
  hound: 7,
};
const PARTICLE_COUNT: Record<GpuVfxKind, number> = {
  artillery: 1_536,
  tornado: 6_144,
  flood: 3_072,
  rift: 3_584,
  catrush: 2_560,
  mech: 2_048,
  hound: 2_560,
};
const DURATION: Record<GpuVfxKind, number> = {
  artillery: 1.7,
  tornado: 4.2,
  flood: 3.6,
  rift: 4,
  catrush: 2.8,
  mech: 3.8,
  hound: 3.1,
};

/**
 * One fixed-capacity storage-buffer particle system shared by every premium
 * interaction. Particles are spawned, integrated and expired entirely in the
 * compute pass; the CPU only updates a small event uniform block.
 */
export class GpuBattleVfx {
  readonly #renderer: THREE.WebGPURenderer;
  readonly #positionsAttribute = new THREE.StorageInstancedBufferAttribute(GPU_VFX_CAPACITY, 4);
  readonly #velocitiesAttribute = new THREE.StorageInstancedBufferAttribute(GPU_VFX_CAPACITY, 4);
  readonly #metaAttribute = new THREE.StorageInstancedBufferAttribute(GPU_VFX_CAPACITY, 4);
  readonly #mesh: THREE.InstancedMesh;
  readonly #time = uniform(0);
  readonly #delta = uniform(0.016);
  readonly #eventSerial = uniform(0);
  readonly #eventKind = uniform(0);
  readonly #eventSign = uniform(1);
  readonly #eventOriginX = uniform(0);
  readonly #eventOriginZ = uniform(0);
  readonly #activeLimit = uniform(0);
  readonly #motionScale = uniform(1);
  readonly #computeNode;
  #serial = 0;
  #startedAt = -10;
  #duration = 0;
  #activeParticles = 0;

  constructor(scene: THREE.Scene, renderer: THREE.WebGPURenderer) {
    this.#renderer = renderer;
    this.#initializeAttributes();

    const positions = storage(this.#positionsAttribute, "vec4", GPU_VFX_CAPACITY);
    const velocities = storage(this.#velocitiesAttribute, "vec4", GPU_VFX_CAPACITY);
    const metadata = storage(this.#metaAttribute, "vec4", GPU_VFX_CAPACITY);

    this.#computeNode = Fn(() => {
      const particle = positions.element(instanceIndex);
      const velocity = velocities.element(instanceIndex);
      const data = metadata.element(instanceIndex);
      const index = float(instanceIndex);
      const enabled = index.lessThan(this.#activeLimit);
      const nextEvent = data.y.notEqual(this.#eventSerial).and(this.#eventSerial.greaterThan(0)).and(enabled);

      If(nextEvent, () => {
        const seed = data.x;
        const seedB = hash(instanceIndex.add(this.#eventSerial.mul(101).toUint()));
        const seedC = hash(instanceIndex.mul(17).add(this.#eventSerial.mul(59).toUint()));
        const angle = seed.mul(TWO_PI);
        const speed = mix(1.25, 5.4, seedB);
        const spread = mix(0.18, 2.9, seedC);

        particle.assign(vec4(
          this.#eventOriginX.add(cos(angle).mul(spread)),
          mix(0.08, 0.72, seedB),
          this.#eventOriginZ.add(sin(angle).mul(spread)),
          1,
        ));
        velocity.assign(vec4(cos(angle).mul(speed), mix(1.5, 5.8, seedC), sin(angle).mul(speed), 0));
        data.y.assign(this.#eventSerial);
        data.z.assign(this.#eventKind);
        data.w.assign(this.#eventSign);

        // Tornado: start as a broad low ring with a strong tangential impulse.
        If(this.#eventKind.greaterThan(1.5).and(this.#eventKind.lessThan(2.5)), () => {
          const radius = mix(0.35, 3.25, seedB);
          particle.xyz.assign(vec3(
            this.#eventOriginX.add(cos(angle).mul(radius)),
            mix(0.08, 1.25, seedC),
            this.#eventOriginZ.add(sin(angle).mul(radius)),
          ));
          velocity.xyz.assign(vec3(sin(angle).negate().mul(speed), mix(1.1, 4.2, seedC), cos(angle).mul(speed)));
        });

        // Flood: seed a full-width crest, then let the compute pass advance it.
        If(this.#eventKind.greaterThan(2.5).and(this.#eventKind.lessThan(3.5)), () => {
          particle.xyz.assign(vec3(
            this.#eventOriginX.add(this.#eventSign.mul(seedB).mul(0.35)),
            mix(0.12, 0.82, seedC),
            mix(-3.65, 3.65, seed),
          ));
          velocity.xyz.assign(vec3(this.#eventSign.mul(mix(1.5, 4.8, seedB)), mix(1.2, 5.6, seedC), sin(angle).mul(0.85)));
        });

        // Cat and robot rushes are directional speed shards rather than radial dust.
        If(this.#eventKind.greaterThan(4.5), () => {
          particle.xyz.assign(vec3(
            this.#eventOriginX.sub(this.#eventSign.mul(seedB).mul(1.35)),
            mix(0.08, 1.65, seedC),
            mix(-2.7, 2.7, seed),
          ));
          velocity.xyz.assign(vec3(this.#eventSign.mul(mix(3.4, 9.5, seedB)), mix(0.4, 4.8, seedC), sin(angle).mul(1.35)));
        });
      });

      If(enabled.not(), () => {
        particle.assign(vec4(10_000, 10_000, 10_000, 0));
      }).ElseIf(particle.w.greaterThan(0), () => {
        const dt = clamp(this.#delta, 0, 0.05).mul(this.#motionScale);
        const kind = data.z;

        // Vortex integration: centripetal pull + tangential acceleration + lift.
        If(kind.greaterThan(1.5).and(kind.lessThan(2.5)), () => {
          const dx = particle.x.sub(this.#eventOriginX);
          const dz = particle.z.sub(this.#eventOriginZ);
          const invRadius = max(0.22, dx.mul(dx).add(dz.mul(dz)).sqrt()).reciprocal();
          velocity.x.addAssign(dz.negate().mul(invRadius).mul(dt).mul(10.5));
          velocity.z.addAssign(dx.mul(invRadius).mul(dt).mul(10.5));
          velocity.x.subAssign(dx.mul(dt).mul(1.15));
          velocity.z.subAssign(dz.mul(dt).mul(1.15));
          velocity.y.addAssign(dt.mul(1.25));
        }).Else(() => {
          velocity.y.subAssign(dt.mul(kind.greaterThan(2.5).and(kind.lessThan(3.5)).select(5.2, 8.8)));
          velocity.xyz.mulAssign(max(0.86, float(1).sub(dt.mul(0.7))));
        });

        particle.xyz.addAssign(velocity.xyz.mul(dt));
        If(particle.y.lessThan(0.055).and(velocity.y.lessThan(0)), () => {
          particle.y.assign(0.055);
          velocity.y.assign(abs(velocity.y).mul(0.42));
          velocity.xz.mulAssign(0.8);
        });
        const lifetime = mix(1.35, 3.8, step(1.5, kind)).mul(mix(0.72, 1.22, data.x));
        particle.w.subAssign(dt.div(lifetime));
      });
    })().compute(GPU_VFX_CAPACITY).setName("Pajama Shared GPU VFX");

    const renderPositions = positions.toAttribute();
    const renderMeta = metadata.toAttribute();
    const life = clamp(renderPositions.w, 0, 1);
    const envelope = sin(life.mul(PI)).pow(0.55);
    const kind = renderMeta.z;
    const seed = renderMeta.x;

    const material = new THREE.SpriteNodeMaterial();
    material.name = "shared-gpu-vfx-sprite-material";
    material.blending = THREE.AdditiveBlending;
    material.depthWrite = false;
    material.positionNode = renderPositions;
    material.scaleNode = vec2(mix(0.028, 0.095, seed).mul(envelope));
    material.rotationNode = seed.mul(TWO_PI).add(life.mul(4));

    const teamColor = mix(vec3(1, 0.3, 0.16), vec3(0.16, 0.95, 1), step(0, renderMeta.w));
    let particleColor = mix(teamColor, vec3(1, 0.82, 0.32), 0.38);
    particleColor = mix(particleColor, vec3(0.74, 0.84, 1), step(1.5, kind).mul(step(kind, 2.5)));
    particleColor = mix(particleColor, vec3(0.1, 0.85, 1), step(2.5, kind).mul(step(kind, 3.5)));
    particleColor = mix(particleColor, vec3(1, 0.58, 0.12), step(3.5, kind).mul(step(kind, 4.5)));
    particleColor = mix(particleColor, vec3(1, 0.72, 0.27), step(4.5, kind).mul(step(kind, 5.5)));
    particleColor = mix(particleColor, vec3(0.4, 0.95, 1), step(5.5, kind).mul(step(kind, 6.5)));
    particleColor = mix(particleColor, vec3(0.95, 0.3, 1), step(6.5, kind));
    material.colorNode = particleColor.mul(mix(0.82, 1.72, envelope));
    const circle = step(uv().xy.sub(0.5).length(), 0.5);
    material.opacityNode = circle.mul(envelope).mul(0.72).mul(life.greaterThan(0).select(1, 0));

    this.#mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), material, GPU_VFX_CAPACITY);
    this.#mesh.name = "shared-gpu-vfx-8192";
    this.#mesh.frustumCulled = false;
    this.#mesh.renderOrder = 10;
    scene.add(this.#mesh);
    this.#renderer.compute(this.#computeNode);
  }

  trigger(kind: GpuVfxKind, sign: number, time: number, originX: number, originZ = 0): void {
    this.#serial += 1;
    this.#eventSerial.value = this.#serial;
    this.#eventKind.value = KIND_ID[kind];
    this.#eventSign.value = Math.sign(sign) || 1;
    this.#eventOriginX.value = originX;
    this.#eventOriginZ.value = originZ;
    this.#activeLimit.value = PARTICLE_COUNT[kind];
    this.#startedAt = time;
    this.#duration = DURATION[kind];
    this.#activeParticles = PARTICLE_COUNT[kind];
  }

  update(dt: number, time: number, reducedMotion: boolean): void {
    this.#time.value = time;
    this.#delta.value = Math.min(0.05, Math.max(0, dt));
    this.#motionScale.value = reducedMotion ? 0.3 : 1;
    if (time - this.#startedAt > this.#duration) this.#activeParticles = 0;
    this.#renderer.compute(this.#computeNode);
  }

  reset(): void {
    this.#serial += 1;
    this.#eventSerial.value = this.#serial;
    this.#activeLimit.value = 0;
    this.#startedAt = -10;
    this.#duration = 0;
    this.#activeParticles = 0;
  }

  snapshot(): { capacity: number; activeParticles: number; computePasses: number } {
    return { capacity: GPU_VFX_CAPACITY, activeParticles: this.#activeParticles, computePasses: 1 };
  }

  dispose(): void {
    this.#mesh.removeFromParent();
    this.#mesh.geometry.dispose();
    (this.#mesh.material as THREE.Material).dispose();
    this.#positionsAttribute.dispose();
    this.#velocitiesAttribute.dispose();
    this.#metaAttribute.dispose();
  }

  #initializeAttributes(): void {
    const positions = this.#positionsAttribute.array as Float32Array;
    const velocities = this.#velocitiesAttribute.array as Float32Array;
    const metadata = this.#metaAttribute.array as Float32Array;
    for (let index = 0; index < GPU_VFX_CAPACITY; index += 1) {
      positions[index * 4] = 10_000;
      positions[index * 4 + 1] = 10_000;
      positions[index * 4 + 2] = 10_000;
      positions[index * 4 + 3] = 0;
      velocities[index * 4] = 0;
      velocities[index * 4 + 1] = 0;
      velocities[index * 4 + 2] = 0;
      velocities[index * 4 + 3] = 0;
      metadata[index * 4] = scatter01(index + 31);
      metadata[index * 4 + 1] = 0;
      metadata[index * 4 + 2] = 0;
      metadata[index * 4 + 3] = 1;
    }
    this.#positionsAttribute.needsUpdate = true;
    this.#velocitiesAttribute.needsUpdate = true;
    this.#metaAttribute.needsUpdate = true;
  }
}

function scatter01(value: number): number {
  let result = value | 0;
  result = Math.imul(result ^ (result >>> 16), 0x21f0aaad);
  result = Math.imul(result ^ (result >>> 15), 0x735a2d97);
  return ((result ^ (result >>> 15)) >>> 0) / 0xffffffff;
}
